import { useEffect, useState } from "react";
import StartingOptions from "./StartingOptions";
import DeletingCommentConfirmation from "./DeletingCommentConfirmation";
import UnsuccessfulDeleteComment from "./UnsuccessfulDeleteComment";
import CommentDeletedSuccessfully from "./CommentDeletedSuccessfully";
import { SERVICE_MODALS_OPTIONS_STATES } from "../../ServiceFunctionsModal.types";
import {
    getReportReasons,
    postReportComment,
} from "@infrastructure/Services/ReportService";
import {
    ReportClassType,
    ReportReason,
} from "@infrastructure/Services/ReportService.types";
import ReportingConfirmation from "../ReportOptionsModal/ReportingConfirmation";
import ReportedSuccessfully from "../ReportOptionsModal/ReportedSuccessfully";
import UnsuccessfulReporting from "../ReportOptionsModal/UnsuccessfulReporting";
import { PostCommentsInfo } from "@infrastructure/Services/PostCommentsService.types";
import { ResourceSuspended } from "../GeneralErrorsModal/ResourceSuspended";
import {
    RESPONSE_CODE_LIMIT_EXCEEDED,
    RESPONSE_CODE_NOT_AUTHORIZED,
    RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED,
    RESPONSE_CODE_PROFILE_DEACTIVATED,
    RESPONSE_CODE_RESOURCE_NOT_FOUND,
    RESPONSE_CODE_RESOURCE_SUSPENDED,
} from "@infrastructure/BackendClientCodes";
import { SuccessfullyCopiedToClipboard } from "../SuccessfullyCopiedToClipboard";
import {
    useInterruptableAction,
    UserLoginActionState,
    useUserStore,
} from "@infrastructure/Persistence/UserState";
import { useIsCurrentUserId } from "@infrastructure/Hooks/UserInteractions/useUserId";
import {
    RESET_API_CACHE_REASONS,
    resetIgnoreApiCacheTime,
} from "@infrastructure/Services/ApiUtility";
import PreviewStartingOptions from "./PreviewStartingOptions";
import { publish } from "@infrastructure/Events/EventBus";
import {
    LATEST_COMMENT_DELETED,
    LATEST_COMMENT_REPORTED,
} from "../../../Post/PostForm/events";
import { useUserCommentedPostsStore } from "@infrastructure/Hooks/UserInteractions/useUserCommentedPostsStore";

const FIFTEEN_MINUTES = 15 * 60 * 1000;

export default function CommentOptionsModalState({
    selectedComment,
    setOpen,
    onCommentReplyOptionClickHandler,
    handleEditingComment,
    setModalCurrentAction,
    modalCurrentAction,
    onCommentDeleteSuspendSuccess,
    updateCommentPost,
    createReactOnCommentFunction,
    createReactOnPreviewComment,
    isLatestComment,
}: any) {
    const [reportReasons, setReportReasons] = useState([] as ReportReason[]);
    const [isLoading, setIsLoading] = useState(true);
    const [showReportOptionsErrorLabel, setShowReportOptionsErrorLabel] =
        useState(false);
    const [isEditAvailable, setIsEditAvailable] = useState(true);

    useEffect(() => {
        const currentTime = new Date().getTime();
        const itemCreationTime = new Date(
            selectedComment.commentContent.date,
        ).getTime();
        const timeDifference = currentTime - itemCreationTime;

        if (timeDifference >= FIFTEEN_MINUTES) {
            setIsEditAvailable(false);
        } else {
            setIsEditAvailable(true);
            const timer = setTimeout(() => {
                setIsEditAvailable(false);
            }, FIFTEEN_MINUTES - timeDifference);

            return () => {
                clearTimeout(timer);
            };
        }
    }, [selectedComment]);

    const loadReportReasons = function () {
        getReportReasons(ReportClassType.CONTENT)
            .then((reportReasons) => {
                setReportReasons(reportReasons);
                setIsLoading(false);
            })
            .catch(() => {
                setModalCurrentAction(
                    SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT,
                );
            });
    };

    useEffect(() => {
        if (
            modalCurrentAction ==
            SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT
        ) {
            loadReportReasons();
        }
    }, [modalCurrentAction]);

    const { setUserAuthContextState } = useUserStore();
    const [isReporting, setIsReporting] = useState(false);
    const [reasonId, setReasonId] = useState<number | undefined>();

    useEffect(() => {
        if (reasonId && isReporting) {
            setIsLoading(true);
            handleReportSubmit();
        }
    }, [reasonId, isReporting]);

    const cancelReporting = () => {
        setIsReporting(false);
    };

    useInterruptableAction(isReporting, handleReportSubmit, cancelReporting);

    function handleReportSubmit() {
        if (!reasonId) {
            setShowReportOptionsErrorLabel(true);
        } else {
            setIsLoading(true);
            setShowReportOptionsErrorLabel(false);
            resetIgnoreApiCacheTime(RESET_API_CACHE_REASONS.COMMENT_REPORT);
            postReportComment(
                reasonId.toString(),
                selectedComment.commentContent.id,
            )
                .then((response: PostCommentsInfo) => {
                    updateCommentPost?.(selectedComment.postId);
                    onCommentDeleteSuspendSuccess?.(response);
                    setModalCurrentAction(
                        SERVICE_MODALS_OPTIONS_STATES.SUCCESSFUL_CONTENT_REPORT,
                    );
                    setReasonId(undefined);
                    if (isLatestComment) {
                        publish({
                            name: LATEST_COMMENT_REPORTED,
                            data: { postId: selectedComment.postId },
                        });
                    }
                })
                .catch((reason) => {
                    setIsLoading(false);
                    if (
                        reason.response?.status ===
                            RESPONSE_CODE_RESOURCE_SUSPENDED ||
                        reason.response?.status ===
                            RESPONSE_CODE_RESOURCE_NOT_FOUND
                    ) {
                        setModalCurrentAction(
                            SERVICE_MODALS_OPTIONS_STATES.REPORTED_CONTENT_RESTRICTED,
                        );
                    } else if (
                        reason.response?.status &&
                        ![
                            RESPONSE_CODE_PROFILE_DEACTIVATED,
                            RESPONSE_CODE_NOT_AUTHORIZED,
                            RESPONSE_CODE_PRIVACY_POLICY_UNACCEPTED,
                            RESPONSE_CODE_LIMIT_EXCEEDED,
                        ].includes(reason.response?.status)
                    ) {
                        setModalCurrentAction(
                            SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT,
                        );
                    } else if (
                        reason.response?.status === RESPONSE_CODE_LIMIT_EXCEEDED
                    ) {
                        setOpen(false);
                    }
                });
        }
    }

    const { removeFromCommentedPosts } = useUserCommentedPostsStore();

    function onSelectedCommentSuccessDeleteHandler(response) {
        onCommentDeleteSuspendSuccess?.(response);
        updateCommentPost?.(selectedComment.postId);
        removeFromCommentedPosts(selectedComment.postId);
        if (isLatestComment) {
            publish({
                name: LATEST_COMMENT_DELETED,
                data: { postId: selectedComment.postId },
            });
        }
    }

    const isOwnComment = useIsCurrentUserId(
        selectedComment.commentContent.user.uuid,
    );
    const reactOnComment = createReactOnPreviewComment
        ? createReactOnPreviewComment(
              selectedComment.commentContent.id.toString(),
              selectedComment.postId,
          )
        : createReactOnCommentFunction?.(
              selectedComment.commentContent.id.toString(),
          );

    function onReportConfirmation(selectedId: number) {
        setIsReporting(true);
        setUserAuthContextState(UserLoginActionState.REPORT_CONTENT);
        setReasonId(selectedId);
    }
    const isEditorial = selectedComment.commentContent.isEditorial;
    switch (modalCurrentAction) {
        case SERVICE_MODALS_OPTIONS_STATES.REPORTING_CONTENT: {
            return ReportingConfirmation({
                reportReasons,
                isLoading,
                showReportOptionsErrorLabel,
                onReportConfirmation,
            });
        }
        case SERVICE_MODALS_OPTIONS_STATES.SUCCESSFUL_CONTENT_REPORT: {
            return ReportedSuccessfully();
        }
        case SERVICE_MODALS_OPTIONS_STATES.REPORTED_CONTENT_RESTRICTED: {
            return ResourceSuspended();
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_CONTENT_REPORT: {
            return UnsuccessfulReporting();
        }
        case SERVICE_MODALS_OPTIONS_STATES.DELETING_COMMENT: {
            return DeletingCommentConfirmation(
                onSelectedCommentSuccessDeleteHandler,
                setModalCurrentAction,
                selectedComment,
            );
        }
        case SERVICE_MODALS_OPTIONS_STATES.TEXT_SUCCESSFULLY_COPIED_TO_CLIPBOARD: {
            return SuccessfullyCopiedToClipboard({ setOpen });
        }
        case SERVICE_MODALS_OPTIONS_STATES.DELETED_COMMENT: {
            return CommentDeletedSuccessfully();
        }
        case SERVICE_MODALS_OPTIONS_STATES.UNSUCCESSFUL_DELETING_COMMENT: {
            return UnsuccessfulDeleteComment();
        }
        case SERVICE_MODALS_OPTIONS_STATES.COMMENT_STARTING_OPTIONS: {
            return StartingOptions({
                selectedComment,
                setOpen,
                onCommentReplyOptionClickHandler,
                setModalCurrentAction,
                modalCurrentAction,
                onCommentDeleteSuspendSuccess,
                handleEditingComment,
                reactOnComment,
                isEditAvailable,
                isOwnComment,
                isEditorial,
            });
        }
        case SERVICE_MODALS_OPTIONS_STATES.COMMENT_PREVIEW_STARTING_OPTIONS: {
            return PreviewStartingOptions({
                selectedComment,
                setOpen,
                onCommentReplyOptionClickHandler,
                setModalCurrentAction,
                modalCurrentAction,
                onCommentDeleteSuspendSuccess,
                handleEditingComment,
                isEditAvailable,
                isOwnComment,
                isEditorial,
            });
        }
    }
}
